import React, { useEffect, useState } from 'react';
import { Star, Check, X, Trash2, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Stars = ({ n = 5 }) => (
  <div className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} className={`h-4 w-4 ${i < n ? 'text-[#CE8946] fill-[#CE8946]' : 'text-gray-300'}`} />)}</div>
);

const ReviewsTab = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = () => {
    axios.get(`${API_URL}/api/admin/reviews`, { headers })
      .then((r) => setReviews(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try {
      if (action === 'delete') await axios.delete(`${API_URL}/api/admin/reviews/${id}`, { headers });
      else await axios.post(`${API_URL}/api/admin/reviews/${id}/${action}`, {}, { headers });
      toast.success(action === 'approve' ? 'Approved & published to the site.' : action === 'reject' ? 'Review rejected.' : 'Review deleted.');
      load();
    } catch (e) { toast.error('Action failed. Please try again.'); }
  };

  const pending = reviews.filter((r) => r.status === 'pending');
  const decided = reviews.filter((r) => r.status !== 'pending');

  const Card = ({ r }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid={`review-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-bold text-[#003456]">{r.name} {r.location && <span className="text-gray-400 font-normal text-sm">· {r.location}</span>}</p>
          <div className="mt-1"><Stars n={r.rating} /></div>
        </div>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.status === 'approved' ? 'bg-green-100 text-green-700' : r.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>{r.status}</span>
      </div>
      <p className="text-gray-700 text-sm mt-3 whitespace-pre-wrap leading-relaxed">{r.review}</p>
      {r.subject && <p className="text-gray-400 text-xs mt-2">From: {r.subject}</p>}
      <div className="flex gap-2 mt-4">
        {r.status !== 'approved' && (
          <button onClick={() => act(r.id, 'approve')} className="inline-flex items-center gap-1 text-sm font-semibold bg-[#CE8946] text-[#003456] px-3 py-1.5 rounded-md hover:bg-[#B87333]" data-testid={`review-approve-${r.id}`}><Check className="h-4 w-4" /> Approve & publish</button>
        )}
        {r.status !== 'rejected' && (
          <button onClick={() => act(r.id, 'reject')} className="inline-flex items-center gap-1 text-sm font-semibold border border-gray-300 text-gray-600 px-3 py-1.5 rounded-md hover:bg-gray-50" data-testid={`review-reject-${r.id}`}><X className="h-4 w-4" /> Reject</button>
        )}
        <button onClick={() => act(r.id, 'delete')} className="inline-flex items-center gap-1 text-sm font-semibold border border-red-200 text-red-600 px-3 py-1.5 rounded-md hover:bg-red-50" data-testid={`review-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="reviews-tab">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-6 w-6 text-[#CE8946]" />
        <h2 className="text-2xl font-bold text-[#003456]">Customer Reviews</h2>
      </div>
      {loading ? <p className="text-gray-500">Loading…</p> : (
        <>
          <div>
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Pending approval ({pending.length})</h3>
            {pending.length === 0 ? <p className="text-gray-500 text-sm">No pending reviews. Submissions from the site appear here for approval.</p> : (
              <div className="grid md:grid-cols-2 gap-4">{pending.map((r) => <Card key={r.id} r={r} />)}</div>
            )}
          </div>
          {decided.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3 mt-8">Approved & past reviews</h3>
              <div className="grid md:grid-cols-2 gap-4">{decided.map((r) => <Card key={r.id} r={r} />)}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReviewsTab;
